import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

interface PaginationProps {
  total: number;
  limit: number;
  offset: number;
  onPageChange: (newOffset: number) => void;
  onLimitChange?: (newLimit: number) => void;
  limitOptions?: number[];
  className?: string;
}

export const Pagination: React.FC<PaginationProps> = ({
  total,
  limit,
  offset,
  onPageChange,
  onLimitChange,
  limitOptions = [5, 10, 20, 50],
  className,
}) => {
  const totalPages = Math.ceil(total / limit);
  const currentPage = Math.floor(offset / limit) + 1;

  if (total === 0) return null;

  const pages = [];
  const maxVisiblePages = 5;

  let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
  let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

  if (endPage - startPage + 1 < maxVisiblePages) {
    startPage = Math.max(1, endPage - maxVisiblePages + 1);
  }

  for (let i = startPage; i <= endPage; i++) {
    pages.push(i);
  }

  const handleLimitChange = (newLimit: number) => {
    if (onLimitChange) {
      onLimitChange(newLimit);
      onPageChange(0);
    }
  };

  return (
    <div
      className={`flex flex-col gap-4 px-4 py-4 sm:px-6 ${className ?? 'mt-4 border-t border-slate-200 dark:border-slate-800'}`}
    >
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <label
            htmlFor="items-per-page"
            className="text-xs font-medium text-slate-600 dark:text-slate-400"
          >
            Itens por página:
          </label>
          <select
            id="items-per-page"
            value={limit}
            onChange={(e) => handleLimitChange(Number(e.target.value))}
            className="block w-auto rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white py-1 px-2.5 text-xs font-semibold shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            disabled={!onLimitChange}
          >
            {limitOptions.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
        </div>
        <div>
          <p className="text-xs text-slate-600 dark:text-slate-400">
            Mostrando <span className="font-bold text-slate-900 dark:text-white">{offset + 1}</span>{' '}
            a{' '}
            <span className="font-bold text-slate-900 dark:text-white">
              {Math.min(offset + limit, total)}
            </span>{' '}
            de <span className="font-bold text-slate-900 dark:text-white">{total}</span> resultados
          </p>
        </div>
      </div>

      {totalPages > 1 && (
        <div className="flex sm:flex-1 sm:items-center sm:justify-center">
          <nav
            className="isolate inline-flex -space-x-px rounded-xl shadow-sm bg-white dark:bg-slate-900 overflow-hidden border border-slate-200 dark:border-slate-800"
            aria-label="Pagination"
          >
            <button
              onClick={() => onPageChange(Math.max(0, offset - limit))}
              disabled={offset === 0}
              className="relative inline-flex items-center px-3 py-2 text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800 focus:z-20 disabled:opacity-40 transition-colors"
            >
              <span className="sr-only">Anterior</span>
              <ChevronLeft className="h-4 w-4" />
            </button>

            {startPage > 1 && (
              <>
                <button
                  onClick={() => onPageChange(0)}
                  className="relative inline-flex items-center px-3.5 py-2 text-xs font-bold text-slate-700 dark:text-slate-300 border-l border-slate-200 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800"
                >
                  1
                </button>
                {startPage > 2 && (
                  <span className="relative inline-flex items-center px-3 py-2 text-xs font-semibold text-slate-400 border-l border-slate-200 dark:border-slate-800">
                    ...
                  </span>
                )}
              </>
            )}

            {pages.map((page) => (
              <button
                key={page}
                onClick={() => onPageChange((page - 1) * limit)}
                className={`relative inline-flex items-center px-3.5 py-2 text-xs font-bold border-l border-slate-200 dark:border-slate-800 transition-colors ${
                  page === currentPage
                    ? 'z-10 bg-primary-600 text-white'
                    : 'text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800'
                }`}
              >
                {page}
              </button>
            ))}

            {endPage < totalPages && (
              <>
                {endPage < totalPages - 1 && (
                  <span className="relative inline-flex items-center px-3 py-2 text-xs font-semibold text-slate-400 border-l border-slate-200 dark:border-slate-800">
                    ...
                  </span>
                )}
                <button
                  onClick={() => onPageChange((totalPages - 1) * limit)}
                  className="relative inline-flex items-center px-3.5 py-2 text-xs font-bold text-slate-700 dark:text-slate-300 border-l border-slate-200 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800"
                >
                  {totalPages}
                </button>
              </>
            )}

            <button
              onClick={() => onPageChange(offset + limit)}
              disabled={offset + limit >= total}
              className="relative inline-flex items-center px-3 py-2 text-slate-500 dark:text-slate-400 border-l border-slate-200 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800 focus:z-20 disabled:opacity-40 transition-colors"
            >
              <span className="sr-only">Próximo</span>
              <ChevronRight className="h-4 w-4" />
            </button>
          </nav>
        </div>
      )}
    </div>
  );
};
